import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Chip,
} from '@mui/material';
import { PlayArrow, Security, Assessment, Visibility } from '@mui/icons-material';
import { useMutation, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { event } from '../analytics';

const CREATE_DEMO_VIEWER = gql`
  mutation CreateDemoViewer {
    createDemoViewer {
      success
      message
      user {
        id
        username
        email
        firstName
        lastName
        isStaff
        isSuperuser
        companyName
        role
      }
    }
  }
`;

const Demo: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createDemoViewer] = useMutation(CREATE_DEMO_VIEWER);
  const navigate = useNavigate();

  const handleLaunchDemo = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await createDemoViewer();
      if (data?.createDemoViewer?.success && data.createDemoViewer.user) {
        event('demo_launch', { event_category: 'engagement', event_action: 'demo_launch' });
        const user = data.createDemoViewer.user;
        localStorage.setItem('verit_authenticated', 'true');
        localStorage.setItem('verit_user', JSON.stringify(user));
        localStorage.setItem('verit_show_demo_welcome', 'true');
        // Force reload to pick up auth state
        window.location.href = '/dashboard';
      } else {
        setError(data?.createDemoViewer?.message || 'Failed to create demo account');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: '#f0fdfa',
      }}
    >
      {/* Left Side */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #134e4a 100%)',
          color: 'white',
          p: 8,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(13, 148, 136, 0.2) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(245, 158, 11, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 600 }}>
          <Typography variant="h2" sx={{ 
            fontWeight: 800, 
            mb: 3, 
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #5eead4 0%, #2d4a6f 50%, #1e3a5f 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            See Verit in Action
          </Typography>
          <Typography variant="h5" sx={{ mb: 5, color: '#99f6e4', fontWeight: 400 }}>
            Explore a live sandbox environment with real AI safety analysis results.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {[
              { icon: <Security />, title: 'Multi-Layer Analysis', desc: 'AI Risk Analysis, Standards & Regulatory Compliance Analysis, and User Perception & Experience Analysis working together' },
              { icon: <Assessment />, title: 'Real Results', desc: 'Pre-populated with realistic AI evaluation data across HR, Healthcare, and Finance' },
              { icon: <Visibility />, title: 'Full Dashboard', desc: 'Heatmaps, risk categories, alerts, compliance reports, and audit logs' },
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ 
                  bgcolor: 'rgba(13, 148, 136, 0.15)', 
                  borderRadius: 2, 
                  p: 1, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#5eead4',
                }}>
                  {item.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#a7f3d0' }}>{item.title}</Typography>
                  <Typography variant="body2" sx={{ color: '#e0f2fe' }}>{item.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(13, 148, 136, 0.12) 0%, transparent 70%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.08) 0%, transparent 70%)',
          }}
        />
      </Box>

      {/* Right Side */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 480,
            width: '100%',
            p: 5,
            borderRadius: 3,
            border: '1px solid #e2e8f0',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #0f172a 0%, #134e4a 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Verit Compliance
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Interactive Sandbox Demo
            </Typography>
          </Box>

          <Box sx={{ 
            bgcolor: '#f0fdfa', 
            borderRadius: 2, 
            p: 3, 
            mb: 4,
            border: '1px solid #ccfbf1',
          }}>
            <Typography variant="body2" sx={{ color: '#134e4a', mb: 2 }}>
              A temporary read-only viewer account will be created for you to explore the platform.
              No sign-up or personal information required.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="Read-only access" size="small" sx={{ bgcolor: '#ccfbf1', color: '#134e4a' }} />
              <Chip label="No sign-up needed" size="small" sx={{ bgcolor: '#ccfbf1', color: '#134e4a' }} />
              <Chip label="Pre-loaded data" size="small" sx={{ bgcolor: '#ccfbf1', color: '#134e4a' }} />
            </Box>
          </Box>

          {error && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
              <Typography variant="body2" color="error">{error}</Typography>
            </Box>
          )}

          <Button
            fullWidth
            data-ga-label="Launch Demo"
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
            onClick={handleLaunchDemo}
            disabled={loading}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 700,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #1e3a5f 0%, #134e4a 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2d4a6f 0%, #1e5f54 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 8px 24px rgba(13, 148, 136, 0.35)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {loading ? 'Setting up your demo...' : 'Launch Demo'}
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 3 }}
          >
            Already have an account?{' '}
            <Typography
              component="a"
              href="/login"
              variant="caption"
              data-ga-label="Sign in (from demo)"
              sx={{ color: '#134e4a', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Sign in
            </Typography>
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default Demo;
